import {
  type FormEvent,
  type ReactNode,
  type RefObject,
  useCallback,
  useId,
  useRef,
  useState,
} from "react";

import {
  type DiagnosticAnswer,
  type ReportRequest,
  type ReportResponse,
  useWriteReport,
} from "@/lib/api";
import { logger } from "@/lib/logger";
import { cn } from "@/lib/utils";

import { getLeadContext } from "../attribution";
import {
  GATE_COMPANY,
  GATE_EMAIL,
  GATE_FAILED,
  GATE_FINE_PRINT_LEAD,
  GATE_FINE_PRINT_TAIL,
  GATE_FIRST_NAME,
  GATE_FREE_EMAIL_NOTE,
  GATE_HEADING,
  GATE_LAST_NAME,
  GATE_LEDE,
  GATE_PREPARING,
  GATE_PRIVACY_LINK,
  GATE_RATE_LIMITED,
  GATE_RETRY,
  GATE_ROLE,
  GATE_ROLE_PLACEHOLDER,
  GATE_SUBMIT,
  PRIVACY_URL,
} from "../copy";
import {
  EMPTY_GATE_VALUES,
  errorStatus,
  firstInvalidField,
  type GateErrors,
  type GateField,
  type GateValues,
  isFreeEmailDomain,
  ROLE_OPTIONS,
  validateGate,
} from "../gate";
import type { LeadSummary } from "../state";
import { HwButton } from "./hw-button";
import { TypingDots } from "./typing-dots";

interface GateFormProps {
  /** The finished interview, sent with the form and rescored server-side. */
  answers: DiagnosticAnswer[];
  /**
   * Called with the written report, which replaces this card. The lead is the
   * only part of the form that outlives it: the first name the report greets
   * and the company the poster is titled with.
   */
  onReport: (lead: LeadSummary, report: ReportResponse) => void;
  /**
   * Brings a field into view once it has taken focus. The focus itself never
   * scrolls (inside a frame it would scroll the parent page as well), so the
   * page decides which container moves.
   */
  revealField?: (field: HTMLElement) => void;
}

/** The generated `ReportRequest` types `answers` as a six-tuple. */
const REPORT_ANSWER_COUNT = 6;

const INPUT =
  "bg-hw-paper border-hw-line focus:border-hw-blue focus-visible:outline-hw-blue hw-body h-[42px] w-full rounded-[4px] border px-3 outline-none focus:bg-white focus-visible:outline-2 focus-visible:outline-offset-2";

const LABEL = "text-hw-soft block text-[13px]";

const ERROR_TEXT = "text-destructive mt-1 text-[12px] leading-[1.4]";

const NOTE_TEXT = "text-hw-mute mt-1 text-[12px] leading-[1.4]";

/** Only the six answers the request type accepts, or nothing to send. */
function asAnswerTuple(answers: DiagnosticAnswer[]): ReportRequest["answers"] | null {
  return answers.length === REPORT_ANSWER_COUNT ? (answers as ReportRequest["answers"]) : null;
}

interface FieldProps {
  id: string;
  label: string;
  error?: string;
  /** A soft nudge that never blocks the form, such as the personal email note. */
  note?: string;
  className?: string;
  children: ReactNode;
}

/** Wraps one field with its label, its error and its optional soft note. */
function Field({ id, label, error, note, className, children }: FieldProps) {
  return (
    <div className={className}>
      <label className={LABEL} htmlFor={id}>
        {label}
      </label>
      <div className="mt-1.5">{children}</div>
      {error && (
        <p className={ERROR_TEXT} id={`${id}-error`}>
          {error}
        </p>
      )}
      {note && (
        <p className={NOTE_TEXT} id={`${id}-note`}>
          {note}
        </p>
      )}
    </div>
  );
}

/** The ids a field's `aria-describedby` should point at, or nothing. */
function describedBy(id: string, error?: string, note?: string): string | undefined {
  const ids = [error ? `${id}-error` : "", note ? `${id}-note` : ""].filter(Boolean);
  return ids.length > 0 ? ids.join(" ") : undefined;
}

/**
 * The lead gate: the form that trades contact details for the written report.
 *
 * Validation is deliberately gentle. Only what the backend needs is required,
 * a personal email address earns a note rather than a refusal, and a failed
 * request keeps every value on screen so retrying is one click.
 */
export function GateForm({ answers, onReport, revealField }: GateFormProps) {
  const uid = useId();
  const { mutateAsync: writeReport, isPending } = useWriteReport();

  const [values, setValues] = useState<GateValues>(EMPTY_GATE_VALUES);
  const [errors, setErrors] = useState<GateErrors>({});
  const [failure, setFailure] = useState<string | null>(null);

  const firstNameRef = useRef<HTMLInputElement>(null);
  const lastNameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const companyRef = useRef<HTMLInputElement>(null);
  const roleRef = useRef<HTMLSelectElement>(null);
  // `isPending` only lands on the next render, so a second click in the same
  // tick would send a second report and burn one of the visitor's three. The
  // ref is read and written synchronously, the way the interview locks sends.
  const submittingRef = useRef(false);

  const ids = {
    firstName: `${uid}-first-name`,
    lastName: `${uid}-last-name`,
    email: `${uid}-email`,
    company: `${uid}-company`,
    role: `${uid}-role`,
  };

  const set = useCallback(<K extends keyof GateValues>(key: K, value: GateValues[K]) => {
    setValues((current) => ({ ...current, [key]: value }));
    // Clear the message the moment the field is touched: keeping it while the
    // visitor types reads as if the correction did not land.
    setErrors((current) => {
      if (!(key in current)) {
        return current;
      }
      const next = { ...current };
      delete next[key as GateField];
      return next;
    });
  }, []);

  const submit = useCallback(
    async (current: GateValues) => {
      if (submittingRef.current) {
        return;
      }

      const found = validateGate(current);
      setErrors(found);
      const invalid = firstInvalidField(found);
      if (invalid) {
        // The banner belongs to the request that failed, not to this attempt:
        // leaving it up next to a fresh field error reads as two problems.
        setFailure(null);
        const targets: Record<string, RefObject<HTMLElement | null>> = {
          firstName: firstNameRef,
          lastName: lastNameRef,
          email: emailRef,
          company: companyRef,
          role: roleRef,
        };
        const field = targets[invalid]?.current;
        if (field) {
          field.focus({ preventScroll: true });
          revealField?.(field);
        }
        return;
      }

      const tuple = asAnswerTuple(answers);
      if (!tuple || !current.role) {
        logger.warn("[diagnostic] report failed", "the interview is not complete");
        setFailure(GATE_FAILED);
        return;
      }

      submittingRef.current = true;
      setFailure(null);
      const firstName = current.firstName.trim();
      const company = current.company.trim();
      try {
        const report = await writeReport({
          first_name: firstName,
          last_name: current.lastName.trim(),
          email: current.email.trim(),
          company,
          role: current.role,
          // Submitting is the consent: the notice above the button says so.
          consent: true,
          context: getLeadContext(),
          answers: tuple,
        });
        logger.debug("[diagnostic]", "report written", {
          source: report.source,
          lead: report.lead_status,
        });
        onReport({ firstName, company }, report);
      } catch (error) {
        const status = errorStatus(error);
        logger.warn("[diagnostic] report failed", status);
        setFailure(status === 429 ? GATE_RATE_LIMITED : GATE_FAILED);
      } finally {
        submittingRef.current = false;
      }
    },
    [answers, onReport, revealField, writeReport],
  );

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void submit(values);
  };

  const emailNote = isFreeEmailDomain(values.email) ? GATE_FREE_EMAIL_NOTE : undefined;

  if (isPending) {
    return (
      <div className="bg-hw-card border-hw-line mt-6 rounded-[16px] border p-5">
        <p className="hw-mono">{GATE_PREPARING}</p>
        <div className="mt-2">
          <TypingDots />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-hw-card border-hw-line mt-6 rounded-[16px] border p-5">
      <h2 className="hw-subheading">{GATE_HEADING}</h2>
      <p className="text-hw-mute mt-1.5 text-[13px] leading-[1.5]">{GATE_LEDE}</p>

      {/*
        data-hs-do-not-collect: the server submits this lead to BOI's own HubSpot
        form. Without the attribute, HubSpot's tracking script would also
        capture it as a separate auto-created "non-HubSpot form".
      */}
      <form
        className="mt-4 grid grid-cols-1 gap-3 min-[640px]:grid-cols-2"
        data-hs-do-not-collect="true"
        noValidate
        onSubmit={onSubmit}
      >
        <Field id={ids.firstName} label={GATE_FIRST_NAME} error={errors.firstName}>
          <input
            aria-describedby={describedBy(ids.firstName, errors.firstName)}
            aria-invalid={errors.firstName ? true : undefined}
            autoComplete="given-name"
            className={INPUT}
            id={ids.firstName}
            name="firstName"
            onChange={(event) => set("firstName", event.target.value)}
            ref={firstNameRef}
            required
            type="text"
            value={values.firstName}
          />
        </Field>

        <Field id={ids.lastName} label={GATE_LAST_NAME} error={errors.lastName}>
          <input
            aria-describedby={describedBy(ids.lastName, errors.lastName)}
            aria-invalid={errors.lastName ? true : undefined}
            autoComplete="family-name"
            className={INPUT}
            id={ids.lastName}
            name="lastName"
            onChange={(event) => set("lastName", event.target.value)}
            ref={lastNameRef}
            required
            type="text"
            value={values.lastName}
          />
        </Field>

        <Field id={ids.email} label={GATE_EMAIL} error={errors.email} note={emailNote}>
          <input
            aria-describedby={describedBy(ids.email, errors.email, emailNote)}
            aria-invalid={errors.email ? true : undefined}
            autoComplete="email"
            className={INPUT}
            id={ids.email}
            name="email"
            onChange={(event) => set("email", event.target.value)}
            ref={emailRef}
            required
            type="email"
            value={values.email}
          />
        </Field>

        <Field id={ids.company} label={GATE_COMPANY} error={errors.company}>
          <input
            aria-describedby={describedBy(ids.company, errors.company)}
            aria-invalid={errors.company ? true : undefined}
            autoComplete="organization"
            className={INPUT}
            id={ids.company}
            name="company"
            onChange={(event) => set("company", event.target.value)}
            ref={companyRef}
            required
            type="text"
            value={values.company}
          />
        </Field>

        <Field id={ids.role} label={GATE_ROLE} error={errors.role}>
          <select
            aria-describedby={describedBy(ids.role, errors.role)}
            aria-invalid={errors.role ? true : undefined}
            className={cn(INPUT, "pr-2")}
            id={ids.role}
            name="role"
            onChange={(event) => set("role", event.target.value as GateValues["role"])}
            ref={roleRef}
            required
            value={values.role}
          >
            <option value="">{GATE_ROLE_PLACEHOLDER}</option>
            {ROLE_OPTIONS.map((role) => (
              <option key={role} value={role}>
                {role}
              </option>
            ))}
          </select>
        </Field>

        <div className="min-[640px]:col-span-2">
          <p className="text-hw-mute text-[12px] leading-[1.45]">
            {GATE_FINE_PRINT_LEAD}
            <a
              className="hover:text-hw-blue underline underline-offset-2"
              href={PRIVACY_URL}
              rel="noopener noreferrer"
              target="_blank"
            >
              {GATE_PRIVACY_LINK}
            </a>
            {GATE_FINE_PRINT_TAIL}
          </p>
        </div>

        <div className="min-[640px]:col-span-2">
          <HwButton disabled={isPending} type="submit">
            {GATE_SUBMIT}
          </HwButton>
        </div>
      </form>

      {failure && (
        <div className="mt-4" role="alert">
          <p className="text-destructive text-[13px] leading-[1.45]">{failure}</p>
          <HwButton className="mt-3" onClick={() => void submit(values)} variant="secondary">
            {GATE_RETRY}
          </HwButton>
        </div>
      )}
    </div>
  );
}
